import { useCallback, useEffect, useState } from "react";
import {
    updateRoomPrivacy,
    db
} from "../../firebase";
import Swal from "sweetalert2";
import { User } from "../AuthContext";
import { Globe, Lock, LogOut, Menu, Sparkles, Users, X } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";

type ActionButtonsProps = {
    isModerator: boolean;
    isMobile: boolean;
    roomId: string;
    user: User | null;
    isLeavingRoom: boolean;
    isMeetingLoaded?: boolean;
    setShowInviteModal: (show: boolean) => void;
    setIsPanelOpen: (open: boolean) => void;
    handleLeaveMeeting: () => void;
    onAddVirtualAvatar?: () => void;
    pendingApprovalsCount?: number;
};

const ActionButtons: React.FC<ActionButtonsProps> = ({
    isModerator,
    isMobile,
    roomId,
    user,
    isLeavingRoom,
    isMeetingLoaded,
    setShowInviteModal,
    setIsPanelOpen,
    handleLeaveMeeting,
    onAddVirtualAvatar,
    pendingApprovalsCount = 0,
}) => {
    const [isTogglingPrivacy, setIsTogglingPrivacy] = useState(false);
    const [isPublic, setIsPublic] = useState<boolean | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLandscape, setIsLandscape] = useState(false);

    useEffect(() => {
        const roomRef = doc(db, "rooms", roomId);
        const unsubscribe = onSnapshot(roomRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const roomData = docSnapshot.data();
                setIsPublic(roomData.isPublic);
            }
        });

        return () => unsubscribe();
    }, [roomId]);

    useEffect(() => {
        const checkOrientation = () => {
            setIsLandscape(window.innerHeight < window.innerWidth && window.innerWidth < 1024);
        };

        checkOrientation();
        window.addEventListener('resize', checkOrientation);
        window.addEventListener('orientationchange', checkOrientation);

        return () => {
            window.removeEventListener('resize', checkOrientation);
            window.removeEventListener('orientationchange', checkOrientation);
        };
    }, []);

    const handleTogglePrivacy = useCallback(async () => {
        if (!isModerator || isTogglingPrivacy || !user) {
            return;
        }

        setIsTogglingPrivacy(true);
        const willBePublic = !isPublic;

        try {
            await updateRoomPrivacy(roomId, willBePublic, user.uid);
            
            // Se a sala foi tornada pública, mostrar modal com link
            if (willBePublic) {
                const roomLink = `${window.location.origin}/sala/${roomId}`;
                
                Swal.fire({
                    icon: 'success',
                    title: 'Sala Liberada!',
                    html: `
                        <p class="mb-4">Sua sala agora é pública. Compartilhe o link abaixo com seus convidados:</p>
                        <div class="bg-gray-100 p-3 rounded-lg border border-gray-300 mb-3">
                            <code class="text-sm break-all">${roomLink}</code>
                        </div>
                    `,
                    showCancelButton: true,
                    confirmButtonText: 'Copiar Link',
                    cancelButtonText: 'Fechar',
                    confirmButtonColor: '#2563eb',
                    cancelButtonColor: '#6b7280',
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigator.clipboard.writeText(roomLink).then(() => {
                            Swal.fire({
                                icon: 'success',
                                title: 'Link Copiado!',
                                text: 'O link da sala foi copiado para a área de transferência.',
                                timer: 2000,
                                showConfirmButton: false,
                            });
                        }).catch(() => {
                            Swal.fire({
                                icon: 'error',
                                title: 'Erro ao Copiar',
                                text: 'Não foi possível copiar o link automaticamente. Por favor, copie manualmente.',
                            });
                        });
                    }
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Erro ao alterar privacidade',
                text: 'Não foi possível alterar a privacidade da sala. Por favor, tente novamente mais tarde.',
            });
        } finally {
            setIsTogglingPrivacy(false);
        }
    }, [isModerator, isTogglingPrivacy, roomId, isPublic, user]);

    if (!isMeetingLoaded) {
        return null;
    }

    if (isMobile) {
        return (
            <>
                {/* Botão hamburguer fixo no canto inferior direito */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="fixed bottom-4 right-4 p-4 bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg transition-colors z-30"
                    title="Menu"
                >
                    <Menu className="w-6 h-6 text-white" />
                </button>

                {/* Menu deslizante */}
                {isMobileMenuOpen && (
                    <>
                        {/* Overlay para fechar o menu */}
                        <div
                            className="fixed inset-0 bg-black/50 z-40 cursor-pointer"
                            onClick={() => setIsMobileMenuOpen(false)}
                            role="button"
                            aria-label="Fechar menu"
                        />

                        {/* Menu lateral */}
                        <div className="fixed bottom-0 left-0 right-0 bg-blue-950/98 backdrop-blur-md border-t border-blue-800/50 shadow-2xl z-50 rounded-t-2xl max-h-[85vh]">
                            <div className="relative">
                                {/* Botão de fechar */}
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="absolute top-3 right-3 p-2 bg-white hover:bg-gray-100 rounded-full transition-colors shadow-lg z-10"
                                    aria-label="Fechar menu"
                                >
                                    <X className="w-5 h-5 text-gray-700" />
                                </button>

                                {/* Indicador visual do menu */}
                                <div className="w-12 h-1 bg-gray-400 rounded-full mx-auto mt-3 mb-2"></div>
                            </div>

                            <div className={`px-4 pb-4 overflow-y-auto min-h-0 ${isLandscape ? 'pt-2 grid grid-cols-2 gap-3' : 'pt-3 space-y-3'}`}>

                                {isModerator && (
                                    <>
                                        {/* Botão de privacidade */}
                                        <button
                                            onClick={() => {
                                                handleTogglePrivacy();
                                                setIsMobileMenuOpen(false);
                                            }}
                                            disabled={isTogglingPrivacy || isPublic === null}
                                            className={`w-full p-3 rounded-lg transition-colors flex items-center justify-center space-x-2 ${isPublic
                                                ? "bg-green-600 hover:bg-green-700"
                                                : "bg-blue-600 hover:bg-blue-700"
                                                } ${isTogglingPrivacy ? "opacity-50 cursor-not-allowed" : ""} text-white`}
                                        >
                                            {isTogglingPrivacy ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            ) : isPublic ? (
                                                <>
                                                    <Globe className="w-5 h-5" />
                                                    <span className={isLandscape ? "text-sm" : "text-base"}>Tornar Sala Privada</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Lock className="w-5 h-5" />
                                                    <span className={isLandscape ? "text-sm" : "text-base"}>Tornar Sala Pública</span>
                                                </>
                                            )}
                                        </button>

                                        {/* Botão de convidar - TEMPORARIAMENTE OCULTO */}
                                        {/* <button
                                            onClick={() => {
                                                setShowInviteModal(true);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center space-x-3 text-white"
                                        >
                                            <UserPlus className="w-5 h-5" />
                                            <span>Convidar Usuário</span>
                                        </button> */}

                                        <button
                                            onClick={() => {
                                                if (onAddVirtualAvatar) {
                                                    onAddVirtualAvatar();
                                                }
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center space-x-2 text-white"
                                        >
                                            <Sparkles className="w-5 h-5" />
                                            <span className={isLandscape ? "text-sm" : "text-base"}>Adicionar Avatar</span>
                                        </button>

                                        {/* Botão de participantes */}
                                        <button
                                            onClick={() => {
                                                setIsPanelOpen(true);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full p-3 bg-blue-800 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center space-x-2 text-white"
                                        >
                                            <Users className="w-5 h-5" />
                                            <span className={isLandscape ? "text-sm" : "text-base"}>Ver Participantes</span>
                                            {pendingApprovalsCount > 0 && (
                                                <span className="ml-1 inline-flex items-center justify-center text-xs font-semibold bg-red-500 text-white px-2 py-0.5 rounded-full">
                                                    {Math.min(pendingApprovalsCount, 99)}
                                                </span>
                                            )}
                                        </button>
                                    </>
                                )}

                                {/* Botão de sair */}
                                <button
                                    onClick={() => {
                                        handleLeaveMeeting();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    disabled={isLeavingRoom}
                                    className={`w-full p-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${isLeavingRoom
                                        ? "bg-red-400 cursor-not-allowed"
                                        : "bg-red-600 hover:bg-red-700"
                                        } text-white ${isLandscape ? "col-span-2" : ""}`}
                                >
                                    {isLeavingRoom ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            <span className={isLandscape ? "text-sm" : "text-base"}>Saindo...</span>
                                        </>
                                    ) : (
                                        <>
                                            <LogOut className="w-5 h-5" />
                                            <span className={isLandscape ? "text-sm" : "text-base"}>Sair da Sala</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }

    return (
        <div className="flex items-center justify-center space-x-3">
            {isModerator && (
                <>
                    <button
                        onClick={handleTogglePrivacy}
                        disabled={isTogglingPrivacy || isPublic === null}
                        className={`p-3 rounded-full transition-colors ${isPublic
                            ? "bg-green-600/80 hover:bg-green-700"
                            : "bg-blue-600/80 hover:bg-blue-700"
                            } ${isTogglingPrivacy ? "opacity-50 cursor-not-allowed" : ""}`}
                        title={isPublic ? "Tornar sala privada" : "Tornar sala pública"}
                    >
                        {isTogglingPrivacy ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : isPublic ? (
                            <Globe className="w-5 h-5 text-white" />
                        ) : (
                            <Lock className="w-5 h-5 text-white" />
                        )}
                    </button>
                    {/* TEMPORARIAMENTE OCULTO */}
                    {/* <button
                        onClick={() => setShowInviteModal(true)}
                        className="p-3 bg-blue-600/80 hover:bg-blue-700 rounded-full transition-colors"
                        title="Convidar usuário por e-mail"
                    >
                        <UserPlus className="w-5 h-5 text-white" />
                    </button> */}
                    <button
                        onClick={onAddVirtualAvatar}
                        className="p-3 bg-blue-600/80 hover:bg-blue-700 rounded-full transition-colors"
                        title="Adicionar avatar virtual"
                    >
                        <Sparkles className="w-5 h-5 text-white" />
                    </button>
                    <button
                        onClick={() => setIsPanelOpen(true)}
                        className="relative p-3 bg-blue-800/80 hover:bg-blue-700 rounded-full transition-colors"
                    >
                        <Users className="w-5 h-5 text-white" />
                        {pendingApprovalsCount > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-[10px] font-semibold bg-red-500 text-white rounded-full">
                                {Math.min(pendingApprovalsCount, 99)}
                            </span>
                        )}
                    </button>
                </>
            )}
            <button
                onClick={handleLeaveMeeting}
                disabled={isLeavingRoom}
                className={`px-4 py-2.5 rounded-full font-medium flex items-center space-x-2 text-sm transition-colors ${isLeavingRoom
                    ? "bg-red-400 cursor-not-allowed"
                    : "bg-red-600/90 hover:bg-red-700"
                    } text-white`}
            >
                {isLeavingRoom ? (
                    <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saindo...</span>
                    </>
                ) : (
                    <>
                        <LogOut className="w-4 h-4" />
                        <span>Sair</span>
                    </>
                )}
            </button>
        </div>
    );
};

export default ActionButtons;